import {
  DEFAULT_THEME,
  themeFromPrimary,
  type EmailTheme,
} from "../data/colorThemes.data";
import type { TemplateZones } from "../data/sampleEmailTemplates.data";

export const DEFAULT_ZONES: TemplateZones = {
  greeting: "<p>Hi <strong>{{name}}</strong>,</p>",
  body: "",
  closing: `<p>Best regards,<br/>The {{company_name}} Team</p>`,
};

export const DEFAULT_HEADER_TITLE = "Email Template";
export const DEFAULT_HEADER_SUBTITLE = "";

const THEME_COMMENT_TAG_RE = /<!--theme:(\{[\s\S]*?\})-->/;

export const parseThemeFromHtml = (html: string): EmailTheme => {
  if (!html) return { ...DEFAULT_THEME };
  const match = html.match(THEME_COMMENT_TAG_RE);
  if (match) {
    try {
      const parsed = JSON.parse(match[1]);
      return { ...DEFAULT_THEME, ...parsed };
    } catch {
      /* fall through */
    }
  }
  const accentMatch = html.match(/linear-gradient\(135deg,(#[0-9a-fA-F]{6})/);
  if (accentMatch) return themeFromPrimary(accentMatch[1]);
  return { ...DEFAULT_THEME };
};

const stripTags = (s: string) =>
  s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();

export const parseHeaderFromHtml = (
  html: string,
): { title: string; subtitle: string; eyebrow: string } => {
  if (!html)
    return {
      title: DEFAULT_HEADER_TITLE,
      subtitle: DEFAULT_HEADER_SUBTITLE,
      eyebrow: "",
    };

  const h1 = html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  const title = h1 ? stripTags(h1[1]) : DEFAULT_HEADER_TITLE;

  const sub = html.match(
    /<!--header-subtitle-->([\s\S]*?)<!--\/header-subtitle-->/i,
  );
  let subtitle = "";
  if (sub) {
    subtitle = stripTags(sub[1]);
  } else if (h1) {
    const afterH1Start = html.indexOf(h1[0]) + h1[0].length;
    const bodyMarker = html.indexOf("<!--zone:greeting-->", afterH1Start);
    const heroCellEnd = html.indexOf("</td>", afterH1Start);
    const stops = [bodyMarker, heroCellEnd].filter((i) => i >= 0);
    const heroEnd = stops.length > 0 ? Math.min(...stops) : afterH1Start;
    const heroAfterH1 = html.slice(afterH1Start, heroEnd);
    const firstPAfter = heroAfterH1.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
    if (firstPAfter) subtitle = stripTags(firstPAfter[1]);
  }
  if (!subtitle) subtitle = "";

  const eyebrowMatch = html.match(
    /<!--header-eyebrow-->([\s\S]*?)<!--\/header-eyebrow-->/i,
  );
  let eyebrow = "";
  if (eyebrowMatch) {
    eyebrow = stripTags(eyebrowMatch[1]);
  } else if (h1) {
    const beforeH1 = html.slice(0, html.indexOf(h1[0]));
    const eyebrowTd = beforeH1.match(
      /<td[^>]*letter-spacing:\s*0\.16em[^>]*>([\s\S]*?)<\/td>/i,
    );
    if (eyebrowTd) eyebrow = stripTags(eyebrowTd[1]);
  }

  return { title, subtitle, eyebrow };
};

const stripFullHtmlWrappers = (html: string): string => {
  if (typeof DOMParser === "undefined") return html;
  const looksLikeFullDoc =
    /<!doctype|<html|<body|<table\s+role=["']presentation/i.test(html);
  if (!looksLikeFullDoc) return html.trim();

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const bodyCell = doc.querySelector("td.et-body");
    if (bodyCell) return bodyCell.innerHTML.trim();

    const allCells = Array.from(doc.querySelectorAll("td"));
    const bestCell = allCells
      .filter(
        (td) =>
          /padding/i.test(td.getAttribute("style") || "") &&
          td.querySelector("p"),
      )
      .sort(
        (a, b) => (b.innerHTML?.length || 0) - (a.innerHTML?.length || 0),
      )[0];
    if (bestCell) return bestCell.innerHTML.trim();

    if (doc.body) return doc.body.innerHTML.trim();
  } catch {
    /* fall through */
  }
  return html.trim();
};

const extractFirstHeading = (html: string): { title: string; rest: string } => {
  const h1 = html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (!h1) return { title: "", rest: html };
  return {
    title: h1[1].replace(/<[^>]+>/g, "").trim(),
    rest: html.replace(h1[0], ""),
  };
};

export const parseZonesFromHtml = (html: string): TemplateZones => {
  if (!html) return { ...DEFAULT_ZONES };

  const greetMatch = html.match(
    /<!--zone:greeting-->([\s\S]*?)<!--\/zone:greeting-->/,
  );
  const bodyMatch = html.match(/<!--zone:body-->([\s\S]*?)<!--\/zone:body-->/);
  const closeMatch = html.match(
    /<!--zone:closing-->([\s\S]*?)<!--\/zone:closing-->/,
  );
  if (greetMatch && bodyMatch && closeMatch) {
    return {
      greeting: greetMatch[1],
      body: bodyMatch[1],
      closing: closeMatch[1],
    };
  }

  let inner = stripFullHtmlWrappers(html);
  inner = extractFirstHeading(inner).rest.trim();

  const paragraphs = inner.match(/<p[^>]*>[\s\S]*?<\/p>/gi) || [];
  let greeting: any = DEFAULT_ZONES.greeting;
  let closing = DEFAULT_ZONES.closing;
  let body = inner;

  if (paragraphs.length >= 2) {
    const firstP: any = paragraphs[0];
    const lastP = paragraphs[paragraphs.length - 1];
    const isGreeting = /^<p[^>]*>(\s*(hi|hello|dear|hey|greetings|good))/i.test(
      firstP,
    );
    const isClosing = /(regards|sincerely|thanks|cheers|warm)/i.test(lastP);

    if (isGreeting) {
      greeting = firstP;
      body = inner.replace(firstP, "");
    }
    if (isClosing && lastP !== firstP) {
      closing = lastP;
      body = body.replace(lastP, "");
    }
  }

  return {
    greeting,
    body: body.trim() || DEFAULT_ZONES.body,
    closing,
  };
};

export const buildEmailHtml = (
  zones: TemplateZones,
  theme: EmailTheme,
  headerTitle: string,
  headerSubtitle: string,
  headerEyebrow = "",
): string => {
  const themeJson = JSON.stringify({
    primary: theme.primary,
    canvasBg: theme.canvasBg,
    bodyBg: theme.bodyBg,
    bodyText: theme.bodyText,
    mutedText: theme.mutedText,
    linkColor: theme.linkColor,
    sectionBg: theme.sectionBg,
    footerBg: theme.footerBg,
    footerText: theme.footerText,
    headerText: theme.headerText,
  });
  return `<!doctype html>
<!--theme:${themeJson}-->
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<meta name="color-scheme" content="light"/>
<meta name="supported-color-schemes" content="light"/>
<title>${headerTitle}</title>
<style>
  body{margin:0;padding:0;background:#eef2f7;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;}
  a{color:${theme.linkColor};}
  table.et-table{width:100%;margin:16px 0;border:1px solid #e2e8f0;border-radius:8px;border-collapse:separate;border-spacing:0;overflow:hidden;background-color:#ffffff;}
  table.et-table td{padding:14px 16px;vertical-align:middle;border-bottom:1px solid #e2e8f0;font-family:'Inter',Arial,sans-serif;}
  table.et-table tr:last-child td{border-bottom:none;}
  table.et-table tr td:first-child{width:42%;background-color:#f8fafc;color:#475569;font-size:12px;line-height:18px;font-weight:600;text-align:center;text-transform:uppercase;letter-spacing:0.06em;border-right:1px solid #e2e8f0;}
  table.et-table tr td:last-child{background-color:#ffffff;color:#0f172a;font-size:13px;line-height:20px;font-weight:500;text-align:left;}
  table.et-table tr td:last-child p{margin:0;text-align:left;}
  table.et-table tr td:first-child ul, table.et-table tr td:first-child ol{margin:0;padding:0;list-style:none;}
  table.et-table tr td:first-child li{display:inline;}
  @media only screen and (max-width:620px){
    .email-card{width:100% !important;border-radius:0 !important;}
    .email-pad{padding-left:18px !important;padding-right:18px !important;}
  }
</style>
</head>
<body style="background:#eef2f7;margin:0;padding:0;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#eef2f7;">
    <tr><td align="center" style="padding:32px 16px;">
      <table role="presentation" class="email-card" width="600" cellpadding="0" cellspacing="0" border="0" style="background:${theme.bodyBg};border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;width:600px;max-width:600px;">
        <tr><td style="height:6px;background:${theme.primary};font-size:0;line-height:0;">&nbsp;</td></tr>
        <tr><td style="padding:28px 28px 24px;border-bottom:1px solid #e2e8f0;text-align:center;background:${theme.bodyBg};">
          <img src="{{logoUrl}}" alt="{{productName}}" height="44" style="display:inline-block;height:44px;width:auto;max-width:70%;vertical-align:middle;border:0;outline:none;font:800 22px/44px 'Inter',Arial,sans-serif;color:#0f172a;letter-spacing:0.04em;text-transform:uppercase;" />
        </td></tr>
        <tr><td style="padding:40px 28px 36px;background:${theme.canvasBg};text-align:center;">
          ${
            headerEyebrow
              ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto 14px;"><tr><td style="border-top:1px solid ${theme.primary};opacity:0.4;width:32px;font-size:0;line-height:0;">&nbsp;</td><td style="padding:0 14px;font:700 11px/16px 'Inter',Arial,sans-serif;color:${theme.primary};text-transform:uppercase;letter-spacing:0.16em;white-space:nowrap;"><!--header-eyebrow-->${headerEyebrow}<!--/header-eyebrow--></td><td style="border-top:1px solid ${theme.primary};opacity:0.4;width:32px;font-size:0;line-height:0;">&nbsp;</td></tr></table>`
              : ""
          }
          <h1 style="margin:0 0 14px;font:800 26px/34px 'Inter',Arial,sans-serif;color:#0f172a;letter-spacing:-0.02em;">${headerTitle}</h1>
          ${headerSubtitle ? `<!--header-subtitle--><p style="margin:0 auto;max-width:460px;font:400 14px/22px 'Inter',Arial,sans-serif;color:#475569;">${headerSubtitle}</p><!--/header-subtitle-->` : ""}
        </td></tr>
        <tr><td class="email-pad et-body" style="padding:28px;font-size:15px;color:${theme.bodyText};line-height:1.7;background:${theme.bodyBg};">
          <!--zone:greeting-->${zones.greeting}<!--/zone:greeting-->
          <!--zone:body-->${zones.body}<!--/zone:body-->
          <!--zone:closing-->${zones.closing}<!--/zone:closing-->
        </td></tr>
        <tr><td style="height:24px;font-size:0;line-height:0;">&nbsp;</td></tr>
        <tr><td style="padding:24px 28px;background:#f8fafc;text-align:center;border-top:1px solid #e2e8f0;">
          <div style="font:400 12px/18px 'Inter',Arial,sans-serif;color:#64748b;">
            {{footerNote}}<br/>
            ZYVO &middot; A product by {{companyName}}<br/>
            &copy; {{year}} {{companyName}}. All rights reserved.
          </div>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
};
